import type { UserData, LocalStorageData } from '../types'
import { fetchUserData, saveUserData, ApiHttpError, isNetworkError } from './api'

export { isValidKey, generateKey } from './key'

const LOCAL_STORAGE_KEY = 'wlns_data'

/** Default empty user record (legacy data shape preserved). */
export function getDefaultUserData(): UserData {
  return {
    indexKey: '',
    name: '',
    email: '',
    weight: 0,
    gender: '',
    lists: {
      activity: [], // cardio
      operation: [], // weights
      exercise: [], // general exercise
    },
  }
}

/** Backfill any missing fields/lists so older records load cleanly. */
export function ensureDataStructure(data: UserData): UserData {
  const defaults = getDefaultUserData()
  if (!data.lists) data.lists = defaults.lists
  if (!data.lists.activity) data.lists.activity = []
  if (!data.lists.operation) data.lists.operation = []
  if (!data.lists.exercise) data.lists.exercise = []
  if (!data.intake) data.intake = []
  return data
}

/**
 * "Blank" data is one with no entries in any of the three lists. Weight/gender
 * and auth fields are config, not user history, so they don't count.
 */
export function isBlankData(data: UserData): boolean {
  const l = data.lists
  if (!l) return (data.intake?.length ?? 0) === 0
  return (
    (l.activity?.length ?? 0) === 0 &&
    (l.operation?.length ?? 0) === 0 &&
    (l.exercise?.length ?? 0) === 0 &&
    (data.intake?.length ?? 0) === 0
  )
}

// ---- localStorage cache (offline fallback) -------------------------------

export function loadFromLocalStorage(): LocalStorageData | null {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error('Failed to load from localStorage:', e)
  }
  return null
}

export function saveToLocalStorage(data: UserData): void {
  try {
    const storageData: LocalStorageData = { ...data, lastSync: Date.now() }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storageData))
  } catch (e) {
    console.error('Failed to save to localStorage:', e)
  }
}

export function clearLocalStorage(): void {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  } catch (e) {
    console.error('Failed to clear localStorage:', e)
  }
}

// ---- Load / save ---------------------------------------------------------

export type LoadOutcome =
  | { data: UserData; source: 'remote' }
  | { data: UserData; source: 'local-offline'; offlineSince: number }
  | { data: UserData; source: 'default' }

/**
 * Load a user record. Remote is authoritative; localStorage is only used when
 * the network itself fails (offline). HTTP errors propagate so the caller can
 * surface them rather than silently resurrecting stale local data.
 */
export async function loadData(userId: string): Promise<LoadOutcome> {
  try {
    const response = await fetchUserData(userId)
    if (response.success && response.data?.value) {
      const remoteData = ensureDataStructure(response.data.value)
      remoteData.indexKey = userId
      saveToLocalStorage(remoteData)
      return { data: remoteData, source: 'remote' }
    }
    // Reachable but no record → genuinely new user.
    const fresh = ensureDataStructure(getDefaultUserData())
    fresh.indexKey = userId
    return { data: fresh, source: 'default' }
  } catch (e) {
    if (e instanceof ApiHttpError) {
      console.error('Remote load failed (HTTP):', e.status, e.message)
      throw e
    }
    if (isNetworkError(e)) {
      const localData = loadFromLocalStorage()
      if (localData && localData.indexKey === userId && !isBlankData(localData)) {
        const offlineSince = localData.lastSync ?? Date.now()
        console.warn('Offline: using localStorage cache from', new Date(offlineSince).toISOString())
        return { data: ensureDataStructure(localData), source: 'local-offline', offlineSince }
      }
      console.error('Offline and no usable local cache — rethrowing')
      throw e
    }
    console.error('Unexpected load error:', e)
    throw e
  }
}

export type SaveFailureKind = 'blocked' | 'http' | 'network' | 'unknown'

export interface SaveResult {
  success: boolean
  error?: string
  kind?: SaveFailureKind
  status?: number
}

/** Save a record to the remote API, guarding against a blank-wipe. */
export async function saveData(
  userId: string,
  data: UserData,
  existingData: UserData,
): Promise<SaveResult> {
  if (isBlankData(data) && !isBlankData(existingData)) {
    console.error('BLOCKED: attempted to save blank data over existing entries')
    return { success: false, error: 'Cannot save blank data over existing data', kind: 'blocked' }
  }

  try {
    const response = await saveUserData(userId, data)
    if (!response.success) {
      console.error('Remote save rejected:', response.error)
      return { success: false, error: response.error, kind: 'http' }
    }
    return { success: true }
  } catch (e) {
    if (e instanceof ApiHttpError) {
      return { success: false, error: e.message, kind: 'http', status: e.status }
    }
    if (isNetworkError(e)) {
      return { success: false, error: 'Network unavailable', kind: 'network' }
    }
    return { success: false, error: (e as Error)?.message || 'Unknown error', kind: 'unknown' }
  }
}
